'use client';

import { Wand2 } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React from 'react';

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

interface AllysImagineSpaceButtonProps {
  variant?: 'primary' | 'secondary' | 'icon';
  messages?: Record<string, unknown>;
  className?: string;
}

/**
 * Button that navigates to the portal Room Designer.
 * Can be placed next to property images in the Allys theme.
 */
export const AllysImagineSpaceButton: React.FC<AllysImagineSpaceButtonProps> = ({
  variant = 'primary',
  messages,
  className = '',
}) => {
  const pathname = usePathname();
  const spacesMsg = messages?.spaces as Record<string, unknown> | undefined;

  const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
  const locale = match?.[1] ?? 'en';

  const baseStyles = 'inline-flex items-center gap-2 transition-all font-semibold';

  const variantStyles = {
    primary:
      'px-6 py-3 bg-gradient-to-r from-gold-600 to-gold-400 text-stone-950 rounded-full hover:shadow-lg hover:scale-105',
    secondary:
      'px-6 py-3 bg-stone-900 text-gold-500 border border-gold-500/30 rounded-full hover:bg-stone-800 hover:border-gold-500',
    icon: 'p-3 bg-stone-900/80 backdrop-blur-sm text-gold-500 rounded-full hover:bg-gold-500 hover:text-stone-950',
  };

  return (
    <Link
      href={`/${locale}/account/spaces`}
      className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      aria-label="Imagine your space with AI"
    >
      <Wand2 size={20} />
      {variant !== 'icon' && (
        <span>
          {safeGetString(spacesMsg?.imagineYourSpace, 'Imagine Your Space')}
        </span>
      )}
    </Link>
  );
};
